import * as React from 'react'

import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import { isUnassignedFilterOptionId } from '~/client/src/shared/hooks/useFilters/filterOptionIds'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import {
  getResourceFilterCategories,
  getResourceFilterKeys,
  getResourceGroupingOptions,
  getResourceSortingOptions,
  getUniqueValuesBatched,
  IResourceFieldOption,
  toPickerGroupingOptions,
} from '~/client/src/shared/resources/builders/getResourceFieldOptions'
import { buildResourceFieldCatalogFromProperties } from '~/client/src/shared/resources/catalog/buildResourceFieldCatalog'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { useResourceFieldProjector } from '~/client/src/shared/resources/projection/useResourceFieldProjector'
import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'

const emptyFilterValues = (): IResourceFieldOption[] => []

export function useResourceFieldOptions({
  resourceTypeId,
  properties,
  getFilterValues,
  records,
}: {
  resourceTypeId: string
  properties?: IResourceProperty[]
  getFilterValues?: (key: string) => string[]
  records?: IResourceRecord[]
}) {
  const catalog = React.useMemo(
    () => buildResourceFieldCatalogFromProperties(resourceTypeId, properties),
    [resourceTypeId, properties],
  )

  const runtimeContext = useResourceRuntimeContext()
  const fieldProjector = useResourceFieldProjector({
    catalog,
    resourceTypeId,
    runtimeContext,
  })

  const sortOptions = React.useMemo(
    () => getResourceSortingOptions(resourceTypeId, properties, catalog),
    [resourceTypeId, properties, catalog],
  )

  const groupByOptions = React.useMemo(
    () =>
      toPickerGroupingOptions(
        resourceTypeId,
        getResourceGroupingOptions(resourceTypeId, properties, catalog),
      ),
    [resourceTypeId, properties, catalog],
  )

  const filterKeys = React.useMemo(
    () => getResourceFilterKeys(resourceTypeId, properties, catalog),
    [resourceTypeId, properties, catalog],
  )

  const batchedValues = React.useMemo(() => {
    if (getFilterValues || !records) return null
    return getUniqueValuesBatched(records, filterKeys, fieldProjector)
  }, [fieldProjector, getFilterValues, records, filterKeys])

  const valuesFn = React.useCallback(
    (key: string): IResourceFieldOption[] => {
      if (getFilterValues) {
        return getFilterValues(key)
          .map(value => {
            const entry = fieldProjector.fromEntryId(key, value)
            return {
              value: entry.id,
              label: entry.label,
            }
          })
          .sort((a, b) => {
            if (isUnassignedFilterOptionId(a.value)) return 1
            if (isUnassignedFilterOptionId(b.value)) return -1
            return compareLabelsAlphanumeric(a.label, b.label)
          })
      }
      if (batchedValues) return batchedValues.get(key) ?? []
      return emptyFilterValues()
    },
    [fieldProjector, getFilterValues, batchedValues],
  )

  const filterCategories = React.useMemo(
    () =>
      getResourceFilterCategories(
        resourceTypeId,
        valuesFn,
        properties,
        catalog,
      ),
    [resourceTypeId, valuesFn, properties, catalog],
  )

  return { sortOptions, groupByOptions, filterCategories }
}
